import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  MapPin, Clock, Phone, Mail, Compass, ImageIcon, BedDouble, ArrowRight, Map as MapIcon, BookOpen,
  Users, Ruler, Eye, Utensils, Shirt, Languages, CreditCard, ShieldCheck, Info, CheckCircle2,
  Sparkles, Coffee, Wallet, KeyRound, Star, MessageSquare,
} from 'lucide-react';
import { PublicBookingWidget } from '@/components/public/PublicBookingWidget';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const bedsText = (beds) => (Array.isArray(beds) && beds.length
  ? beds.map((b) => `${b.count || b.quantity || 1} ${b.type || 'letto'}`).join(' · ') : '');

const fmtReviewDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }); }
  catch { return ''; }
};

/** Riga di 5 stelle (piene / vuote) in base al voto. */
function Stars({ value = 0, className = 'h-4 w-4' }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} su 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${className} ${n <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />
      ))}
    </span>
  );
}

function RoomCard({ room, href }) {
  const img = room?.main_image || (room?.images || [])[0];
  const type = room?.type_custom || room?.type_standard || room?.category || 'Camera';
  const price = room?.base_price;
  const bedroomCount = (room?.bedrooms?.length) || room?.bedrooms_count || 0;
  const allBeds = (room?.bedrooms?.length ? room.bedrooms.flatMap((r) => r.beds || []) : room?.beds) || [];
  const beds = bedsText(allBeds);
  const amenities = (room?.amenities || []).filter(Boolean).slice(0, 4);
  const policies = [
    room?.breakfast_included && { icon: Coffee, label: 'Colazione inclusa' },
    room?.free_cancellation && { icon: ShieldCheck, label: 'Cancellazione gratuita' },
    room?.pay_at_property && { icon: Wallet, label: 'Paghi in struttura' },
  ].filter(Boolean);
  const body = (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={`structure-room-${room?.id}`}>
      <div className="relative h-44 overflow-hidden bg-muted">
        {img ? (
          <img src={mediaUrl(img)} alt={room?.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-8 w-8 text-muted-foreground" /></div>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-background/85 px-2.5 py-1 text-[11px] font-semibold text-foreground backdrop-blur">{type}</span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-semibold text-foreground">{room?.name}</h3>
        {room?.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{room.description}</p>}
        {/* specifiche */}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/80">
          {room?.capacity_total ? <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-primary" />fino a {room.capacity_total}</span> : null}
          {room?.area_sqm ? <span className="inline-flex items-center gap-1"><Ruler className="h-3.5 w-3.5 text-primary" />{room.area_sqm} m²</span> : null}
          {bedroomCount > 0 ? <span className="inline-flex items-center gap-1"><BedDouble className="h-3.5 w-3.5 text-primary" />{bedroomCount} camer{bedroomCount === 1 ? 'a' : 'e'} da letto</span> : null}
          {room?.view && room.view !== 'Nessuna' ? <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-primary" />Vista {room.view}</span> : null}
          {beds ? <span className="inline-flex items-center gap-1"><BedDouble className="h-3.5 w-3.5 text-primary" />{beds}</span> : null}
        </div>
        {/* servizi camera */}
        {amenities.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {amenities.map((a, i) => <span key={i} className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground">{typeof a === 'string' ? a : a.name}</span>)}
          </div>
        )}
        {/* policy */}
        {policies.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1">
            {policies.map((p, i) => <span key={i} className="inline-flex items-center gap-1 text-[11px] font-medium text-primary"><p.icon className="h-3.5 w-3.5" />{p.label}</span>)}
          </div>
        )}
        <div className="mt-auto flex items-end justify-between pt-3">
          <div>
            <span className="font-display text-xl font-bold text-foreground">{euro(price)}</span>
            <span className="text-sm text-muted-foreground"> / {room?.pricing_unit || 'notte'}</span>
          </div>
          {href && <span className="flex items-center gap-1 text-sm font-medium text-primary">Vedi camera <ArrowRight className="h-4 w-4" /></span>}
        </div>
      </div>
    </article>
  );
  return href ? <Link to={href} className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl">{body}</Link> : body;
}

/**
 * Vista "vetrina" di una struttura — presentazionale e riusabile.
 * Usata sia dalla pagina pubblica (/struttura/:id) sia dall'anteprima nel CRM.
 * Pattern generico pensato per essere riutilizzato dai futuri verticali.
 */
export function StructureLandingView({ data, publicMode = false, roomHref, ownerId }) {
  const st = data?.structure || {};
  const gallery = (data?.gallery || []).filter(Boolean);
  const rooms = data?.rooms || [];
  const pois = data?.concierge?.points_of_interest || [];
  const hero = data?.hero_image || gallery[0] || (rooms[0]?.main_image) || '';
  const svc = data?.services || {};
  const op = data?.operativity || {};
  const cancel = data?.cancellation_policy || [];
  const scin = data?.self_check_in || {};
  const brand = data?.brand || {};
  const reviews = data?.reviews || {};
  const hasReviews = (reviews.count || 0) > 0;
  const recentReviews = (reviews.recent || []).filter((r) => (r.comment || '').trim());
  const hasServices = (svc.amenities?.length || svc.languages?.length || svc.accepted_payments?.length || svc.kitchen || svc.laundry);
  const hasGoodToKnow = (st.checkin_time || st.checkout_time || op.min_stay || op.max_stay || op.deposit_percent || scin.enabled || brand.cancellation_text);
  const [lightbox, setLightbox] = useState(null);

  const lat = st.lat ?? data?.concierge?.lat;
  const lng = st.lng ?? data?.concierge?.lng;
  const hasGeo = (lat != null && lat !== '' && lng != null && lng !== '') || !!st.address;
  const q = (lat != null && lat !== '' && lng != null && lng !== '') ? `${lat},${lng}` : encodeURIComponent(st.address || data?.name || '');
  const mapSrc = `https://maps.google.com/maps?q=${q}&z=14&hl=it&output=embed`;

  const linkFor = (room) => {
    if (roomHref) return roomHref(room.id);
    return publicMode ? `/camera/${room.id}` : null;
  };

  return (
    <div data-testid="structure-landing-view">
      {/* HERO */}
      <section className="relative isolate flex min-h-[46vh] items-end overflow-hidden rounded-3xl text-white">
        {hero ? (
          <img src={mediaUrl(hero)} alt={data?.name} className="absolute inset-0 -z-10 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 -z-10 bg-primary/80" />
        )}
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(180deg, hsl(150 42% 8% / 0.25) 0%, hsl(150 42% 8% / 0.55) 55%, hsl(150 42% 8% / 0.9) 100%)' }} />
        <div className="w-full p-6 sm:p-10">
          {data?.tagline && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide backdrop-blur-md">{data.tagline}</span>
          )}
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold leading-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)] sm:text-5xl" data-testid="structure-name">{data?.name || 'Struttura'}</h1>
          {st.address && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-white/90"><MapPin className="h-4 w-4" />{st.address}</p>
          )}
          {hasReviews && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-sm font-semibold backdrop-blur-md" data-testid="structure-rating-badge">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span>{Number(reviews.average || 0).toFixed(1)}</span>
              <span className="text-white/70">·</span>
              <a href="#recensioni" className="font-medium text-white/90 underline-offset-2 hover:underline">{reviews.count} {reviews.count === 1 ? 'recensione' : 'recensioni'}</a>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {rooms.length > 0 && (
              <a href="#camere" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90" data-testid="structure-cta-rooms">
                <BedDouble className="h-4 w-4" />Vedi le camere ({rooms.length})
              </a>
            )}
            {st.email && (
              <a href={`mailto:${st.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/20">
                <Mail className="h-4 w-4" />Contatta la struttura
              </a>
            )}
          </div>
        </div>
      </section>

      {/* INFO RAPIDE */}
      {(st.checkin_time || st.checkout_time || st.phone || st.email) && (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 rounded-2xl border border-border bg-card px-5 py-3 text-sm">
          {st.checkin_time && <span className="flex items-center gap-1.5 text-foreground"><Clock className="h-4 w-4 text-primary" />Check-in {st.checkin_time}</span>}
          {st.checkout_time && <span className="flex items-center gap-1.5 text-foreground"><Clock className="h-4 w-4 text-primary" />Check-out {st.checkout_time}</span>}
          {st.phone && <span className="flex items-center gap-1.5 text-foreground"><Phone className="h-4 w-4 text-primary" />{st.phone}</span>}
          {st.email && <span className="flex items-center gap-1.5 text-foreground"><Mail className="h-4 w-4 text-primary" />{st.email}</span>}
        </div>
      )}

      {/* AVVISO B&B — host residente */}
      {data?.host_resident && (
        <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-3 text-sm" data-testid="structure-host-resident">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-foreground/90"><span className="font-semibold">B&amp;B a conduzione familiare:</span> l&rsquo;host vive nella struttura insieme agli ospiti.</p>
        </div>
      )}

      {/* PRENOTAZIONE ONLINE (solo sito pubblico) */}
      {publicMode && ownerId && rooms.length > 0 && (
        <PublicBookingWidget ownerId={ownerId} structureName={data?.name} />
      )}

      {/* STORIA */}
      {data?.story && (
        <section className="mt-8" data-testid="structure-story">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><BookOpen className="h-5 w-5 text-primary" />La nostra storia</h2>
          <p className="mt-3 max-w-3xl whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{data.story}</p>
        </section>
      )}

      {/* GALLERY (immagini generali) */}
      {gallery.length > 0 && (
        <section className="mt-8" data-testid="structure-gallery">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><ImageIcon className="h-5 w-5 text-primary" />Immagini</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {gallery.map((g, i) => (
              <button key={i} type="button" onClick={() => setLightbox(mediaUrl(g))} className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`structure-gallery-${i}`}>
                <img src={mediaUrl(g)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* CAMERE */}
      {rooms.length > 0 && (
        <section id="camere" className="mt-10 scroll-mt-20" data-testid="structure-rooms">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><BedDouble className="h-5 w-5 text-primary" />Le camere</h2>
          <p className="mt-1 text-sm text-muted-foreground">Scegli la sistemazione e apri la scheda per tutti i dettagli.</p>
          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rooms.map((r) => <RoomCard key={r.id} room={r} href={linkFor(r)} />)}
          </div>
        </section>
      )}

      {/* RECENSIONI */}
      {hasReviews && (
        <section id="recensioni" className="mt-10 scroll-mt-20" data-testid="structure-reviews">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><Star className="h-5 w-5 text-primary" />Recensioni degli ospiti</h2>
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4">
            <span className="font-display text-4xl font-bold text-foreground" data-testid="structure-reviews-average">{Number(reviews.average || 0).toFixed(1)}</span>
            <div className="flex flex-col">
              <Stars value={reviews.average || 0} className="h-4 w-4" />
              <span className="mt-0.5 text-sm text-muted-foreground">Basato su {reviews.count} {reviews.count === 1 ? 'recensione' : 'recensioni'}</span>
            </div>
          </div>
          {recentReviews.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              {recentReviews.map((r, i) => (
                <article key={i} className="flex flex-col rounded-2xl border border-border bg-card p-4" data-testid={`structure-review-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{(r.guest_name || 'O').charAt(0).toUpperCase()}</span>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{r.guest_name || 'Ospite'}</p>
                        {r.created_at && <p className="text-xs text-muted-foreground">{fmtReviewDate(r.created_at)}</p>}
                      </div>
                    </div>
                    <Stars value={r.rating || 0} className="h-3.5 w-3.5" />
                  </div>
                  {r.comment && <p className="mt-3 text-sm leading-relaxed text-foreground/90">&ldquo;{r.comment}&rdquo;</p>}
                  {r.reply && (
                    <div className="mt-3 rounded-xl border border-border bg-muted/40 p-3">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground"><MessageSquare className="h-3.5 w-3.5 text-primary" />Risposta della struttura</p>
                      <p className="mt-1 text-sm text-muted-foreground">{r.reply}</p>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {/* SERVIZI E COMFORT */}
      {hasServices && (
        <section className="mt-10" data-testid="structure-services">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><Sparkles className="h-5 w-5 text-primary" />Servizi e comfort</h2>
          {svc.amenities?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="structure-amenities">
              {svc.amenities.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground"><CheckCircle2 className="h-4 w-4 text-primary" />{a}</span>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/80">
            {svc.kitchen && <span className="inline-flex items-center gap-1.5"><Utensils className="h-4 w-4 text-primary" />Angolo cottura</span>}
            {svc.laundry && <span className="inline-flex items-center gap-1.5"><Shirt className="h-4 w-4 text-primary" />Lavanderia</span>}
            {svc.languages?.length > 0 && <span className="inline-flex items-center gap-1.5"><Languages className="h-4 w-4 text-primary" />Lingue: {svc.languages.join(', ')}</span>}
            {(svc.accepted_payments?.length > 0 || brand.accepted_cards?.length > 0) && (
              <span className="inline-flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-primary" />Pagamenti: {[...(svc.accepted_payments || []), ...(brand.accepted_cards || [])].join(', ')}</span>
            )}
          </div>
        </section>
      )}

      {/* BUONO A SAPERSI */}
      {hasGoodToKnow && (
        <section className="mt-10" data-testid="structure-goodtoknow">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><Info className="h-5 w-5 text-primary" />Buono a sapersi</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(st.checkin_time || st.checkout_time) && (
              <div className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-4">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div><p className="text-sm font-semibold text-foreground">Orari</p><p className="text-xs text-muted-foreground">Check-in dalle {st.checkin_time || '—'} · Check-out entro le {st.checkout_time || '—'}</p></div>
              </div>
            )}
            {(op.min_stay || op.max_stay) && (
              <div className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-4">
                <BedDouble className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div><p className="text-sm font-semibold text-foreground">Soggiorno</p><p className="text-xs text-muted-foreground">Minimo {op.min_stay || 1} nott{(op.min_stay || 1) === 1 ? 'e' : 'i'}{op.max_stay ? ` · massimo ${op.max_stay}` : ''}</p></div>
              </div>
            )}
            {op.deposit_percent ? (
              <div className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-4">
                <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div><p className="text-sm font-semibold text-foreground">Acconto</p><p className="text-xs text-muted-foreground">{op.deposit_percent}% alla conferma</p></div>
              </div>
            ) : null}
            {scin.enabled && (
              <div className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-4">
                <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div><p className="text-sm font-semibold text-foreground">Self check-in</p><p className="text-xs text-muted-foreground">{scin.instructions ? scin.instructions.slice(0, 90) : 'Check-in autonomo disponibile'}</p></div>
              </div>
            )}
            {brand.cancellation_text && (
              <div className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-4 sm:col-span-2 lg:col-span-1">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div><p className="text-sm font-semibold text-foreground">Cancellazione</p><p className="text-xs text-muted-foreground">{brand.cancellation_text}</p></div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* POLITICHE DI CANCELLAZIONE */}
      {cancel.length > 0 && (
        <section className="mt-10" data-testid="structure-cancellation">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><ShieldCheck className="h-5 w-5 text-primary" />Politiche di cancellazione</h2>
          <div className="mt-4 space-y-2">
            {cancel.map((p, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card px-4 py-3">
                <span className="text-sm font-semibold text-foreground">{p.label || 'Politica'}</span>
                <span className="text-xs text-muted-foreground">
                  {p.hours_before ? `Fino a ${p.hours_before}h prima: ` : ''}
                  <span className="font-medium text-primary">{p.refundable_percent != null ? `rimborso ${p.refundable_percent}%` : 'non rimborsabile'}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* MAPPA */}
      {hasGeo && (
        <section className="mt-10" data-testid="structure-map">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><MapIcon className="h-5 w-5 text-primary" />Dove siamo</h2>
          <div className="mt-4 overflow-hidden rounded-2xl border border-border">
            <iframe title="Mappa struttura" src={mapSrc} className="h-[360px] w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
          {st.address && <p className="mt-2 text-sm text-muted-foreground">{st.address}</p>}
        </section>
      )}

      {/* NEI DINTORNI */}
      {pois.length > 0 && (
        <section className="mt-10" data-testid="structure-concierge">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-foreground"><Compass className="h-5 w-5 text-primary" />Nei dintorni</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pois.map((p, i) => (
              <div key={i} className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-4">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{p.name}</p>
                  {p.category && <p className="text-xs text-muted-foreground">{p.category}</p>}
                  {p.description && <p className="mt-1 text-xs leading-relaxed text-foreground/80">{p.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-6" onClick={() => setLightbox(null)} data-testid="structure-lightbox">
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain" />
        </div>
      )}

      {(rooms.length === 0 && !data?.story && gallery.length === 0) && (
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted/40 p-8 text-center text-sm text-muted-foreground" data-testid="structure-empty">
          Questa struttura sta completando la sua vetrina. Torna presto!
        </div>
      )}
    </div>
  );
}

export default StructureLandingView;
